import { useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { profileApi, ProfileModel } from '@pukka/api';
import { constants } from '@pukka/common';
import { useFlagWithFallback } from '@pukka/unleash';
import * as Accordion from '@radix-ui/react-accordion';
import noop from 'lodash/noop';
import { FaChevronRight, FaLock } from 'react-icons/fa';
import { twMerge } from 'tailwind-merge';

import { SidebarRedDot } from './SidebarRedDot';

interface SidebarNavItemProps {
  readonly menus: { [key: string]: ProfileModel.Permission[] };
  readonly name: string;
  readonly isAbleToAccessOnlineStore?: (
    menuName: string,
    isEmailAllowed: boolean,
    isShoppingExtEnabled: boolean,
    isBusinessCategoryAllowed: boolean,
  ) => boolean;
  readonly isAbleToAccessCapital?: (menuName: string, isOwner?: boolean) => boolean;
  readonly isAbleToAccessGoogleOnlineStore?: (
    menuName: string | undefined,
    isEmailAllowed: boolean,
    isGoogleShoppingExtEnabled: boolean,
    isBusinessCategoryAllowed: boolean,
  ) => boolean;
  readonly isAbleToAccessFacebookOnlineStore?: (
    menuName: string | undefined,
    isEmailAllowed: boolean,
    isFacebookShoppingExtEnabled: boolean,
    isBusinessCategoryAllowed: boolean,
  ) => boolean;
  onClickSubMenu?: () => void;
}

function isBusinessCategoryAllowedFn(whitelistAll: boolean, allowedBusinessCategoryIds: number[], myBusinessCategoryId?: number) {
  return whitelistAll || allowedBusinessCategoryIds.some((businessId) => businessId === myBusinessCategoryId);
}

function isEmailAllowedFn(allowedEmails: string[], myEmail?: string) {
  return allowedEmails.some((email) => email === myEmail);
}

function ableToAccessOnlineStore(
  menuName: string,
  isEmailAllowed: boolean,
  isShoppingExtEnabled: boolean,
  isBusinessCategoryAllowed: boolean,
) {
  return menuName !== 'Online Store' || ((isEmailAllowed || isShoppingExtEnabled) && isBusinessCategoryAllowed);
}

function ableToAccessCapital(menuName: string, isOwner?: boolean) {
  return menuName !== 'Capital' || !!isOwner;
}

function ableToAccessGoogleOnlineStore(
  menuName: string | undefined,
  isEmailAllowed: boolean,
  isGoogleShoppingExtEnabled: boolean,
  isBusinessCategoryAllowed: boolean,
) {
  return menuName !== 'Google Shopping' || ((isEmailAllowed || isGoogleShoppingExtEnabled) && isBusinessCategoryAllowed);
}

function ableToAccessFacebookOnlineStore(
  menuName: string | undefined,
  isEmailAllowed: boolean,
  isFacebookShoppingExtEnabled: boolean,
  isBusinessCategoryAllowed: boolean,
) {
  return menuName !== 'Facebook & Instagram Shop' || ((isEmailAllowed || isFacebookShoppingExtEnabled) && isBusinessCategoryAllowed);
}

function SidebarNavItem({
  name,
  menus,
  isAbleToAccessOnlineStore = ableToAccessOnlineStore,
  isAbleToAccessCapital = ableToAccessCapital,
  isAbleToAccessGoogleOnlineStore = ableToAccessGoogleOnlineStore,
  isAbleToAccessFacebookOnlineStore = ableToAccessFacebookOnlineStore,
  onClickSubMenu = noop,
}: SidebarNavItemProps) {
  const { data: profile } = profileApi.useProfile();
  const containRedDots = menus[name].find((menu) => menu.withRedDot);
  const location = useLocation();
  const isNavActive = useCallback((url: string) => !!location.pathname.match(url), [location]);
  const [whitelistAllBusinessCategory] = useFlagWithFallback('enable-whitelist-all-business-category');
  const [enableShoppingExt] = useFlagWithFallback('enable-shopping-ext');
  const [enableGoogleShoppingExt] = useFlagWithFallback('enable-google-shopping-ext');
  const [enableFacebookShoppingExt] = useFlagWithFallback('enable-facebook-shopping-ext');
  const allowedBusinessCategory = isBusinessCategoryAllowedFn(
    whitelistAllBusinessCategory,
    constants.ALLOWED_BUSINESS_CATEGORY_ID,
    profile?.business?.business_category_id,
  );
  const allowedEmail = isEmailAllowedFn(constants.ALLOWED_EMAIL, profile?.email);

  // Check if user is available to access online store
  if (!isAbleToAccessOnlineStore(name, allowedEmail, enableShoppingExt, allowedBusinessCategory)) {
    return null;
  }

  // Can not access capital page if it's not owner
  if (!isAbleToAccessCapital(name, profile?.is_owner)) {
    return null;
  }

  // Render menu with no children
  if (menus[name].length === 1) {
    const isLocked = menus[name][0].is_allowed_for_moka_pay && !profile?.business?.is_mokapos_user;

    return (
      <li>
        <Link
          to={menus?.[name]?.[0].urls?.[0] || ''}
          reloadDocument={!menus[name][0].isPukka}
          className={twMerge(
            'flex w-full items-center p-nav-item font-serif text-xs uppercase text-neutral-400',
            'hover:bg-legacy-600 hover:text-neutral-200 ',
          )}
          onClick={onClickSubMenu}
        >
          {name} {isLocked ? 'locked' : ''}
        </Link>
      </li>
    );
  }

  return (
    <li>
      <Accordion.Trigger
        className={twMerge(
          'group relative flex w-full items-center p-nav-item font-serif text-xs uppercase text-neutral-400',
          'radix-state-open:text-neutral-200',
          'hover:bg-legacy-600  hover:text-neutral-200',
        )}
      >
        <FaChevronRight className="absolute -ml-4 group-radix-state-open:rotate-90" />
        {name} {containRedDots && <SidebarRedDot className="group-radix-state-open:hidden" />}
      </Accordion.Trigger>
      <Accordion.Content>
        <ul>
          {menus[name].map((menu) => {
            const isLocked = !menu.is_allowed_for_moka_pay && !profile?.business?.is_mokapos_user;

            if (
              !isAbleToAccessGoogleOnlineStore(menu.sub_menu, allowedEmail, enableGoogleShoppingExt, allowedBusinessCategory) ||
              !isAbleToAccessFacebookOnlineStore(menu.sub_menu, allowedEmail, enableFacebookShoppingExt, allowedBusinessCategory) ||
              menu.isHidden
            ) {
              return null;
            }

            const menuURL = menu?.urls?.[0] || '';

            return (
              <li key={menu.id}>
                <Link
                  reloadDocument={!menu.isPukka}
                  to={menuURL}
                  className={twMerge(
                    'group relative flex items-center p-nav-item font-serif text-xs text-neutral-400',
                    'cursor-pointer hover:bg-legacy-600 hover:text-light-blue-500',
                    isNavActive(menuURL) && 'bg-legacy-600 text-light-blue-500',
                  )}
                  aria-selected={isNavActive(menuURL)}
                  onClick={onClickSubMenu}
                >
                  <div className="absolute inset-y-0 left-0 w-1 group-hover:bg-neutral-200" />
                  {menu.sub_menu} {menu.withRedDot ? <SidebarRedDot /> : ''} {isLocked ? <FaLock /> : ''}
                </Link>
              </li>
            );
          })}
        </ul>
      </Accordion.Content>
    </li>
  );
}

export {
  SidebarNavItem,
  ableToAccessCapital as _ableToAccessCapital,
  ableToAccessOnlineStore as _ableToAccessOnlineStore,
  ableToAccessGoogleOnlineStore as _ableToAccessGoogleOnlineStore,
  ableToAccessFacebookOnlineStore as _ableToAccessFacebookOnlineStore,
};
